import { useRegisterCustomFontFaces } from '@components/announcement/hooks/useRegisterCustomFontFaces.hooks';
import { AnnouncementDefaultFontFamily } from '@components/announcement/util/announcementDefaultFontFamily.extension';
import {
  ANNOUNCEMENT_DEFAULT_FONT_SIZE,
  AnnouncementDefaultFontSize,
} from '@components/announcement/util/announcementDefaultFontSize.extension';
import { AnnouncementFontSize } from '@components/announcement/util/announcementFontSize.extension';
import { useQuery } from '@tanstack/react-query';
import { type JSONContent } from '@tiptap/core';
import Color from '@tiptap/extension-color';
import FontFamily from '@tiptap/extension-font-family';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { type Editor, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { customFontsQueryOptions } from '@/query/customFontsQueryOptions';
import { compareByLocale } from '@/util';

interface UseAnnouncementRichTextEditorProps {
  value: JSONContent;
  editable: boolean;
  onChange: (value: JSONContent) => void;
}

export const useAnnouncementRichTextEditor = ({
  value,
  editable,
  onChange,
}: UseAnnouncementRichTextEditorProps): Editor | null => {
  const { data: fonts = [] } = useQuery(customFontsQueryOptions);
  useRegisterCustomFontFaces(fonts);

  const { data: defaultFontFamily = '' } = useQuery({
    ...customFontsQueryOptions,
    select: (fontsToSort) =>
      fontsToSort.toSorted(compareByLocale((font) => font.name))[0]?.name,
  });

  const defaultFontFamilyRef = useRef(defaultFontFamily);
  useEffect(() => {
    defaultFontFamilyRef.current = defaultFontFamily;
  }, [defaultFontFamily]);

  const lastFontFamilyRef = useRef('');
  const lastFontSizeRef = useRef('');

  const trackLastUsedFont = useCallback((editorInstance: Editor) => {
    const attrs = editorInstance.getAttributes('textStyle');
    if (attrs.fontFamily) {
      lastFontFamilyRef.current = attrs.fontFamily;
    }
    if (attrs.fontSize) {
      lastFontSizeRef.current = attrs.fontSize;
    }
  }, []);

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        blockquote: false,
        bulletList: false,
        code: false,
        codeBlock: false,
        heading: false,
        horizontalRule: false,
        listItem: false,
        orderedList: false,
        strike: false,
        underline: false,
      }),
      Underline,
      TextAlign.configure({
        types: ['paragraph'],
        alignments: ['left', 'center', 'right'],
      }),
      TextStyle,
      FontFamily,
      AnnouncementFontSize,
      Color,
      Highlight.configure({ multicolor: true }),
      AnnouncementDefaultFontSize.configure({
        getDefaultFontSize: () =>
          lastFontSizeRef.current || ANNOUNCEMENT_DEFAULT_FONT_SIZE,
      }),
      AnnouncementDefaultFontFamily.configure({
        getDefaultFontFamily: () =>
          lastFontFamilyRef.current || defaultFontFamilyRef.current,
      }),
    ],
    [],
  );

  const editor = useEditor({
    extensions,
    content: value,
    editable,
    shouldRerenderOnTransaction: true,
    immediatelyRender: false,
    onUpdate: ({ editor }) => {
      trackLastUsedFont(editor);
      onChange(editor.getJSON());
    },
    onSelectionUpdate: ({ editor }) => {
      trackLastUsedFont(editor);
    },
  });

  return editor;
};
